import { type FC, useState } from 'react';
import { Form } from 'react-bootstrap';

import { type Timeline } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import { figurePropertiesBlock } from './styles';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

interface Props {
	element: Timeline;
	courseId: number;
}

const FigurePropertiesBlock: FC<Props> = (props) => {
	const { element, courseId } = props;
	const { figure_number, title, description, caption, credits, instructions, touchInstructions } =
		element;

	const commonPayload = { elementId: element.family_id, courseId };
	const debouncedDispatch = useDebouncedDispatch();

	const [figureProperties, setFigureProperties] = useState({
		figure_number,
		title,
		description,
		caption,
		credits,
		instructions,
		touchInstructions
	});

	const handleChange = (value) => {
		setFigureProperties({ ...figureProperties, ...value });
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	return (
		<Form className={figurePropertiesBlock}>
			<div className="block">
				<Form.Group className="figure-number">
					<Form.Label>Number</Form.Label>
					<Form.Control
						value={figureProperties.figure_number}
						placeholder="Figure number"
						onChange={(e) => handleChange({ figure_number: e.target.value })}
					/>
				</Form.Group>

				<Form.Group className="figure-title">
					<Form.Label>Title</Form.Label>
					<Form.Control
						value={figureProperties.title}
						placeholder="Figure title"
						onChange={(e) => handleChange({ title: e.target.value })}
					/>
				</Form.Group>
			</div>

			<Form.Group>
				<Form.Label>Instructions</Form.Label>
				<Form.Control
					as="textarea"
					rows={1}
					value={figureProperties.instructions}
					placeholder="Figure instructions"
					onChange={(e) => handleChange({ instructions: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Touch Instructions</Form.Label>
				<Form.Control
					as="textarea"
					rows={1}
					value={figureProperties.touchInstructions}
					placeholder="Figure touch instructions"
					onChange={(e) => handleChange({ touchInstructions: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Description</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.description}
					showTips={false}
					options={{ placeholder: 'Description (for accessibility)' }}
					onChange={(value) => handleChange({ description: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Caption</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.caption}
					showTips={false}
					options={{ placeholder: 'Figure caption' }}
					onChange={(value) => handleChange({ caption: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Credit(s)</Form.Label>
				<CodeMirrorHTML
					value={figureProperties.credits}
					showTips={false}
					options={{ placeholder: 'Figure credits' }}
					onChange={(value) => handleChange({ credits: value })}
				/>
			</Form.Group>
		</Form>
	);
};

export default FigurePropertiesBlock;
